import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog } from "@threadws/jute";
import type { Connection } from "../../../api/generated";
import {
  personaConnectionsListQueryKey,
  personaConnectionsUnlinkMutation,
} from "../../../api/generated/@tanstack/react-query.gen";

interface UnlinkConnectionDialogProps {
  personaId: string;
  /** The connection to unlink. The dialog is open while this is set. */
  connection: Connection | null;
  onClose: () => void;
}

/** Asks before unlinking an account from a persona, then refreshes the persona's connections. */
export function UnlinkConnectionDialog({ personaId, connection, onClose }: UnlinkConnectionDialogProps) {
  const queryClient = useQueryClient();
  const unlink = useMutation({
    ...personaConnectionsUnlinkMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: personaConnectionsListQueryKey({ path: { id: personaId } }) });
      onClose();
    },
  });

  // Closing can't cancel the request, so stay open until it settles.
  const close = () => {
    if (unlink.isPending) return;
    unlink.reset();
    onClose();
  };

  return (
    <Dialog
      open={connection !== null}
      onClose={close}
      title="Unlink account?"
      description={
        connection
          ? `${connection.label} will no longer be used by this persona. Other personas that use it keep it.`
          : undefined
      }
      footer={
        <>
          <Button variant="ghost" icon="" disabled={unlink.isPending} onClick={close}>
            Cancel
          </Button>
          <Button
            icon=""
            disabled={unlink.isPending}
            onClick={() => connection && unlink.mutate({ path: { id: personaId, connectionId: connection.id } })}
          >
            {unlink.isPending ? "Unlinking…" : "Unlink"}
          </Button>
        </>
      }
    >
      {unlink.error && <p className="text-danger">{unlink.error.message}</p>}
    </Dialog>
  );
}
